import { AlertBar } from "@components/ui/AlertBar"
import { Button } from "@components/ui/Button"
import { NonIdealState } from "@components/ui/NonIdealState"
import { Spinner } from "@components/ui/Spinner"
import { useFerve, useFerveSession, useFerveUserId } from "@features/ferve/FerveProvider"
import { type PlannerItem as PlannerItemData, useResolvedPlanner } from "@features/ferve/useResolvedPlanner"
import { useProgramData } from "@features/program/useProgramData"
import { HeroSearchWidget } from "@features/search/cards/HeroSearchWidget"
import { trackEvent } from "@features/tracking/tracking"
import { PlannerItem } from "@features/wishlist/PlannerItem"
import { PlannerShareMode, usePlannerSharing } from "@features/wishlist/usePlannerSharing"
import { CalendarDaysIcon, CheckIcon, LinkIcon } from "@heroicons/react/24/outline"
import { useCreateSharedPlanner } from "@hooks/queries"
import { useDismissable } from "@hooks/useDismissable"
import { downloadIcsFile } from "@utils/ics-file"
import { cn } from "@utils/tw"
import dayjs from "dayjs"
import { Link } from "eddev/routing"
import { AnimatePresence, motion } from "motion/react"
import { ReactNode, useEffect, useMemo, useState } from "react"
import { useFlyingCarpet } from "../useFlyingCarpet"
import { IconCross } from "@components/ui/Icon"

export function PlannerPanel() {
  const loggedIn = useFerveSession()?.loggedIn
  const carpet = useFlyingCarpet()

  const items = useResolvedPlanner()
  const hasItems = items.length > 0

  const program = useProgramData()

  const sharing = usePlannerSharing()

  const contents = useMemo(() => {
    let nodes = [] as ReactNode[]
    let currentDateString = null as string | null
    const today = dayjs().format("ddd D MMMM")
    for (let item of items!) {
      let dateHeading = item.date.format("ddd D MMMM")
      if (dateHeading === today) {
        dateHeading = "Today"
      }
      if (dateHeading !== currentDateString) {
        currentDateString = dateHeading
        nodes.push(
          <motion.h3
            key={dateHeading}
            className="type-title-s pt-5 md:pt-4 pb-2"
            animate={{ opacity: 1, scale: 1 }}
            initial={{ opacity: 0, scale: 0.9 }}
            exit={{ opacity: 0, scale: 0.9 }}
            layout="position"
          >
            {dateHeading}
          </motion.h3>,
        )
      }
      if (item.clashWith?.previous) {
        nodes.push(
          <motion.div
            key={`${item.session.id}-clash-previous`}
            animate={{ opacity: 1, scale: 1 }}
            initial={{ opacity: 0, scale: 0.9 }}
            exit={{ opacity: 0, scale: 0.9 }}
            layout="position"
            className="-my-1 flex type-ui-s gap-[0.5em]"
          >
            <div>
              <div className="text-coral w-[4.7em] flex-none text-right">
                <span className="type-body-xs">Clash</span>
              </div>
            </div>
          </motion.div>,
        )
      }
      nodes.push(
        <motion.div
          key={item.session.id}
          animate={{ opacity: 1, scale: 1 }}
          initial={{ opacity: 0, scale: 0.9 }}
          exit={{ opacity: 0, scale: 0.9 }}
          layout="position"
        >
          <PlannerItem {...item} />
        </motion.div>,
      )
    }
    return nodes
  }, [items])

  const loginPrompt = useDismissable("planner-login-prompt", 1)
  const sharePrompt = useDismissable("planner-share-prompt", 7)

  console.log("Items are ", items)

  return (
    <motion.div layoutRoot layoutScroll data-mixpanel-area="Planner Panel">
      <AnimatePresence presenceAffectsLayout mode="popLayout" initial={false}>
        {program.isPending && hasItems ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <Spinner />
          </div>
        ) : items?.length! > 0 ? (
          <motion.div
            key="contents"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.7 }}
            className="absolute inset-0 bg-bg origin-top flex flex-col"
          >
            <div className="p-[--padding] pt-0 overflow-y-auto flex-1">
              <AnimatePresence presenceAffectsLayout mode="popLayout">
                {!loggedIn && !loginPrompt.dismissed && (
                  <motion.div
                    key="login-alert"
                    animate={{ opacity: 1, scale: 1 }}
                    initial={{ opacity: 0, scale: 0.9 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    layout="position"
                  >
                    <div className="py-3">
                      <AlertBar className="basic-text-styles outline" onDismiss={loginPrompt.onDismiss}>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault()
                            carpet.launch("account")
                          }}
                        >
                          Sign in
                        </a>{" "}
                        to keep your watchlist saved to your account, and keep track of your purchases!
                      </AlertBar>
                    </div>
                  </motion.div>
                )}
                {contents}
                {items.length > 2 && !sharePrompt.dismissed && !sharing.isActive ? (
                  <motion.div
                    key="sharer-prompt"
                    animate={{ opacity: 1, scale: 1 }}
                    initial={{ opacity: 0, scale: 0.9 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    layout="position"
                  >
                    <div className="h-5" />
                    <SharerPrompt onDismiss={sharePrompt.onDismiss} />
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
            {/* Sharing Panel */}
            <AnimatePresence>
              {sharing.isActive && sharing.selectedItems.size > 0 && (
                <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }}>
                  <div className="border-t border-bg-mid bg-bg shadow-[0_0_15px_-2px_rgba(0,0,0,0.2)]">
                    <motion.div
                      initial={{ scale: 0.9, y: 20, opacity: 0 }}
                      animate={{ scale: 1, y: 0, opacity: 1 }}
                      exit={{ scale: 0.9, opacity: 0 }}
                      className="px-[--padding] py-3 flex items-center justify-between"
                    >
                      <FinishShareButton mode={sharing.shareMode!} items={items} />
                    </motion.div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            {/* <div className="px-[--padding] py-3 flex items-stretch justify-stretch border-t border-bg-mid">
              <Button href="/program/" className="w-full" shape="branded" variant="hero">
                View Program
              </Button>
            </div> */}
          </motion.div>
        ) : (
          <motion.div
            key="empty"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.7 }}
            className="p-[--padding] absolute inset-0 bg-bg origin-top"
          >
            <Empty />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

function Empty() {
  return (
    <NonIdealState
      icon="plannerEmpty"
      title="There's nothing in your watchlist"
      description="Look for the bookmark icon on any film to add it to your watchlist."
      actions={
        <>
          <Link href="/program" as={Button} variant="hero" shape="branded">
            View Program
          </Link>
        </>
      }
    />
  )
}

function SharerPrompt(props: { onDismiss: () => void }) {
  return (
    <HeroSearchWidget
      image={{ srcSet: "/wp-content/themes/sff2/assets/story-sharer/example-thumb.png" }}
      title="Share your SFF"
      description="Let the world know what you'll be watching. Don't forget to tag @sydfilmfest!"
      theme="subtle"
      trackingTitle="Share to Story Prompt"
      onDismiss={props.onDismiss}
      buttons={
        <>
          <Button variant="primary" shape="branded" href="/share-to-story/">
            Select and Share
          </Button>
        </>
      }
    />
  )
}

function FinishShareButton(props: { mode: PlannerShareMode; items: PlannerItemData[] }) {
  const ferve = useFerve()
  const sharing = usePlannerSharing()
  const session = useFerveSession()
  const ferveUserId = useFerveUserId()

  const createSharedPlanner = useCreateSharedPlanner()

  const [completionMessage, setCompletionMessage] = useState<string | false>(false)

  useEffect(() => {
    if (completionMessage) {
      const timeout = setTimeout(() => {
        setCompletionMessage(false)
        sharing.cancel()
      }, 2000)
      return () => clearTimeout(timeout)
    }
  }, [completionMessage])

  return (
    <Button
      variant="hero"
      shape="branded"
      className={cn("w-full flex-1 transition-colors")}
      rightIcon={
        completionMessage ? (
          <CheckIcon data-part="icon" className="icon scale-75" />
        ) : props.mode === "calendar" ? (
          <CalendarDaysIcon data-part="icon" className="icon scale-75" />
        ) : (
          <LinkIcon data-part="icon" className="icon scale-75" />
        )
      }
      loading={createSharedPlanner.isPending}
      onClick={async () => {
        if (props.mode === "link") {
          createSharedPlanner
            .mutateAsync({
              ids: [...sharing.selectedItems.values()],
              userId: ferveUserId ?? undefined,
              name: session?.firstName,
            })
            .catch((error) => {
              alert("An error occurred while generating the share link. Please try again later.")
              return null
            })
            .then(async (result) => {
              if (result?.createSharedPlanner?.planner?.id) {
                const url = document.location.origin + "/planner/" + result.createSharedPlanner.planner.id
                trackEvent("Created Shared Planner", {
                  SharedPlannerUrl: "/planner/" + result.createSharedPlanner.planner.id,
                })
                try {
                  await navigator.clipboard.writeText(url)
                  setCompletionMessage("Copied!")
                } catch (error) {
                  await navigator.share({
                    url: url,
                  })
                  sharing.cancel()
                }
              }
            })
        } else {
          trackEvent("Shared Planner as Calendar", {})
          const items = props.items.filter((item) => sharing.selectedItems.has(item.session.info?.ferveId!))
          downloadIcsFile(items, items.length === 1 ? items[0].film.title! : "My SFF Watchlist")
          sharing.cancel()
        }
      }}
    >
      {completionMessage ? completionMessage : props.mode === "link" ? "Share Link" : "Export to Calendar"}
    </Button>
  )
}
